/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
import React, { useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import Blanket from '@atlaskit/blanket';
import { getDocument } from '@atlaskit/browser-apis';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import Portal from '@atlaskit/portal';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ConfirmationCloseModal } from './confirmation-close-modal';
import { ExpandedSection } from './expanded-section';
import { MainSection } from './main-section';
import type { SidebarProps } from './types';

const defaultContainerStyles = xcss({
	position: 'relative',
	height: '100%',
	borderLeft: `${token('border.width', '1px')} solid ${token('color.border', '#DFE1E6')}`,
	backgroundColor: 'elevation.surface',
});

const fullBorderStyles = xcss({
	border: `${token('border.width', '1px')} solid ${token('color.border', '#DFE1E6')}`,
	borderRadius: 'radius.large',
});

const sectionsContainerStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});

const expandedStyles = xcss({
	width: '1000px',
	zIndex: 'modal',

	[media.above.lg]: {
		width: '1416px',
	},

	[media.above.xl]: {
		width: '1560px',
	},
});

const unexpandedStyles = xcss({
	width: '320px',

	[media.above.lg]: {
		width: '400px',
	},

	[media.above.xl]: {
		width: '486px',
	},
});

export function Sidebar({
	testId,
	title,
	titleComponent,
	isLoading = false,
	hasError = false,
	errorHeader,
	errorDescription,
	isExpanded = false,
	expandedContent,
	mainContent,
	footerButtons = [],
	hasUnsavedChanges = false,
	onClose,
	heightOffset,
	isFullBorderEnabled,
	isVisible = true,
}: SidebarProps): React.JSX.Element | null {
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const ref = useRef<null | HTMLElement>(null);
	const triggerElementRef = useRef<HTMLElement | null>(null);
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const previousIsVisibleRef = useRef<boolean>(isVisible);

	// if heightOffset is provided subtract it from the sidebar height
	useEffect(() => {
		if (!ref.current) {
			return;
		}
		if (heightOffset !== undefined) {
			ref.current.style.height = `calc(100vh - ${heightOffset}px)`;
		}
	}, [heightOffset]);

	useEffect(() => {
		const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
		};

		if (!hasUnsavedChanges) {
			return;
		}

		const unbind = bind(window, {
			type: 'beforeunload',
			listener: beforeUnloadHandler,
		});

		return unbind;
	}, [hasUnsavedChanges]);

	// Track the element that triggered the sidebar to open and move focus to sidebar
	useEffect(() => {
		const wasVisible = previousIsVisibleRef.current;
		previousIsVisibleRef.current = isVisible;

		if (isVisible && !wasVisible) {
			// Capture the trigger element before moving focus
			const document = getDocument();
			if (document?.activeElement instanceof HTMLElement) {
				triggerElementRef.current = document.activeElement;
			}

			// Move focus to the close button in the sidebar
			// Use multiple requestAnimationFrame calls to ensure the sidebar is fully rendered
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (closeButtonRef.current) {
						closeButtonRef.current.focus();
					} else if (ref.current) {
						// Fallback: focus the container if close button not found
						if (!ref.current.hasAttribute('tabIndex')) {
							ref.current.setAttribute('tabIndex', '-1');
						}
						ref.current.focus();
					}
				});
			});
		}
	}, [isVisible]);

	if (!isVisible) {
		return null;
	}

	const handleOnClose = () => {
		if (hasUnsavedChanges) {
			setIsConfirmationModalOpen(true);
		} else {
			if (onClose) {
				onClose();
			}
			// Return focus to the trigger element
			if (triggerElementRef.current) {
				triggerElementRef.current.focus();
			}
		}
	};

	const handleModalConfirmClicked = () => {
		setIsConfirmationModalOpen(false);

		if (onClose) {
			onClose();
		}
		// Focus return is handled by the Modal's shouldReturnFocus prop
	};

	const handleModalCancelClicked = () => {
		setIsConfirmationModalOpen(false);
	};

	const handleModalOnClose = () => {
		setIsConfirmationModalOpen(false);
	};

	return (
		<>
			<Box
				testId={testId}
				ref={ref}
				xcss={[
					defaultContainerStyles,
					isFullBorderEnabled ? fullBorderStyles : undefined,
					isExpanded ? expandedStyles : unexpandedStyles,
				]}
			>
				<Box xcss={sectionsContainerStyles}>
					<ExpandedSection hasError={hasError} isExpanded={isExpanded} isLoading={isLoading}>
						{expandedContent}
					</ExpandedSection>
					<MainSection
						title={title}
						titleComponent={titleComponent}
						isLoading={isLoading}
						hasError={hasError}
						isExpanded={isExpanded}
						errorHeader={errorHeader}
						errorDescription={errorDescription}
						mainContent={mainContent}
						footerButtons={footerButtons}
						onClose={handleOnClose}
						closeButtonRef={closeButtonRef}
					>
						{mainContent}
					</MainSection>
				</Box>
			</Box>

			<ConfirmationCloseModal
				isOpen={isConfirmationModalOpen}
				onConfirmClicked={handleModalConfirmClicked}
				onCancelClicked={handleModalCancelClicked}
				onClose={handleModalOnClose}
				shouldReturnFocus={triggerElementRef.current ? triggerElementRef : undefined}
			/>

			<Portal zIndex="unset">{isExpanded && <Blanket isTinted={true} />}</Portal>
		</>
	);
}

export const SidebarSwitch = (sidebarProps: SidebarProps): React.JSX.Element => {
	return sidebarProps.isMotionEnabled ? (
		<SidebarWithMotion {...sidebarProps} />
	) : (
		<Sidebar {...sidebarProps} />
	);
};

export default SidebarSwitch;

export const SidebarWithMotion = (sidebarProps: SidebarProps): React.JSX.Element => {
	const { isVisible, ...props } = sidebarProps;
	return (
		<ExitingPersistence appear>
			{isVisible && (
				<SlideIn enterFrom="right" duration="medium">
					{({ className, ref }) => (
						<div className={className} ref={ref} aria-modal style={{ height: '100%' }}>
							<Sidebar {...props} />
						</div>
					)}
				</SlideIn>
			)}
		</ExitingPersistence>
	);
};
